import type {
  AdminSession,
  CheckoutResponse,
  CreateSessionResult,
  MediaItem,
  MessageItem,
  PostMessageResponse,
  PublicSession,
  SessionSettingsPatch,
  UploadResponse,
} from '@wallflower/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Seconds until a rate-limited request is worth retrying, when the server says. */
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
  }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body && !(init.body instanceof FormData)
        ? { 'content-type': 'application/json' }
        : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as
      | { error?: string; message?: string }
      | null;
    const retryAfter = Number(response.headers.get('retry-after'));
    throw new ApiError(
      response.status,
      body?.error ?? `Request failed (${response.status})`,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    );
  }
  return (await response.json()) as T;
}

export const api = {
  /** A demo wall: no account, and the server fixes the terms. */
  createDemoSession(): Promise<CreateSessionResult> {
    return request('/api/sessions/demo', { method: 'POST', body: JSON.stringify({}) });
  },

  publicSession(sessionId: string): Promise<PublicSession> {
    return request(`/api/sessions/${sessionId}/public`);
  },

  publicMedia(sessionId: string): Promise<{ media: MediaItem[] }> {
    return request(`/api/sessions/${sessionId}/media`);
  },

  publicMessages(sessionId: string): Promise<{ messages: MessageItem[] }> {
    return request(`/api/sessions/${sessionId}/messages`);
  },

  adminSession(
    adminToken: string,
  ): Promise<{ session: AdminSession; media: MediaItem[]; messages: MessageItem[] }> {
    return request(`/api/admin/${adminToken}/session`);
  },

  /** Payment from the control panel: works signed out, like the panel itself. */
  adminCheckout(adminToken: string): Promise<CheckoutResponse> {
    return request(`/api/admin/${adminToken}/checkout`, { method: 'POST' });
  },

  postMessage(sessionId: string, body: string, authorName: string | null) {
    return request<PostMessageResponse>(`/api/sessions/${sessionId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, ...(authorName ? { authorName } : {}) }),
    });
  },

  postHostMessage(adminToken: string, body: string, authorName: string | null) {
    return request<PostMessageResponse>(`/api/admin/${adminToken}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, ...(authorName ? { authorName } : {}) }),
    });
  },

  setMessageHidden(adminToken: string, messageId: string, hidden: boolean) {
    return request<{ message: MessageItem }>(
      `/api/admin/${adminToken}/messages/${messageId}/hidden`,
      { method: 'POST', body: JSON.stringify({ hidden }) },
    );
  },

  deleteMessage(adminToken: string, messageId: string) {
    return request<{ ok: true }>(`/api/admin/${adminToken}/messages/${messageId}`, {
      method: 'DELETE',
    });
  },

  updateSettings(adminToken: string, patch: SessionSettingsPatch) {
    return request<{ settings: AdminSession['settings'] }>(
      `/api/admin/${adminToken}/settings`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    );
  },

  setHidden(adminToken: string, mediaId: string, hidden: boolean) {
    return request<{ media: MediaItem }>(
      `/api/admin/${adminToken}/media/${mediaId}/hidden`,
      { method: 'POST', body: JSON.stringify({ hidden }) },
    );
  },

  deleteMedia(adminToken: string, mediaId: string) {
    return request<{ ok: true }>(`/api/admin/${adminToken}/media/${mediaId}`, {
      method: 'DELETE',
    });
  },
};

export function qrUrl(sessionId: string, size: 'sm' | 'md' | 'lg' = 'md'): string {
  return `/api/sessions/${sessionId}/qr.svg?size=${size}`;
}

/**
 * Uploads with progress. `fetch` still can't report upload progress, and a guest
 * on hotel wifi needs to see that something is happening.
 */
export function uploadFiles(
  sessionId: string,
  files: File[],
  uploaderName: string | null,
  onProgress: (fraction: number) => void,
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    if (uploaderName) form.append('uploaderName', uploaderName);
    for (const file of files) form.append('files', file, file.name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/sessions/${sessionId}/upload`);
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as UploadResponse);
        } catch {
          reject(new ApiError(xhr.status, 'Malformed response from server'));
        }
      } else {
        reject(new ApiError(xhr.status, `Upload failed (${xhr.status})`));
      }
    });
    xhr.addEventListener('error', () => reject(new ApiError(0, 'Network error')));
    xhr.addEventListener('abort', () => reject(new ApiError(0, 'Upload cancelled')));
    xhr.send(form);
  });
}
